import { API_BASE } from '@/shared/services/api';
import { useTranslation } from "react-i18next";import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Badge } from '@/shared/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/shared/components/ui/dialog';
import { toast } from 'sonner';
import {
  Search,
  Plus,
  Filter,
  Users,
  Star,
  Phone,
  Mail,
  MapPin,
  TrendingUp,
  Package,
  Clock,
  CheckCircle2,
  MoreVertical,
  Building2,
  Globe,
  RefreshCw } from
'lucide-react';

const API_URL = API_BASE;

const getAuthHeaders = () => {
  const token = localStorage.getItem('ezerpToken');
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };
};

export const Vendors = () => {const { t } = useTranslation("procurement");
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [newVendor, setNewVendor] = useState({
    name: '',
    code: '',
    category: '',
    contact_email: '',
    contact_phone: '',
    address: ''
  });

  const fetchVendors = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/procurement/vendors`, {
        headers: getAuthHeaders()
      });
      if (!response.ok) throw new Error('Failed to fetch vendors');
      const data = await response.json();
      setVendors(data);
    } catch (err) {
      toast.error('Failed to load vendors');
      setVendors([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVendors();
  }, []);

  const handleAddVendor = async () => {
    if (!newVendor.name || !newVendor.code) {
      toast.error('Name and code are required');
      return;
    }
    try {
      const response = await fetch(`${API_URL}/api/procurement/vendors`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(newVendor)
      });
      if (!response.ok) throw new Error('Failed to create vendor');
      toast.success('Vendor created successfully');
      setShowAddDialog(false);
      setNewVendor({ name: '', code: '', category: '', contact_email: '', contact_phone: '', address: '' });
      fetchVendors();
    } catch (err) {
      toast.error('Failed to create vendor');
    }
  };

  const filteredVendors = vendors.filter((vendor) => {
    const matchesSearch = (vendor.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (vendor.code || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (vendor.category || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' ||
    statusFilter === 'active' && vendor.is_active ||
    statusFilter === 'inactive' && !vendor.is_active;
    return matchesSearch && matchesStatus;
  });

  const activeVendors = vendors.filter((v) => v.is_active).length;
  const totalSpend = vendors.reduce((sum, v) => sum + (v.total_spend || 0), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500" />
      </div>);

  }

  return (
    <div className="space-y-6" data-testid="procurement-vendors">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight font-['Space_Grotesk']">{t("vendorDirectory", "Vendor Directory")}

          </h1>
          <p className="text-muted-foreground mt-1">{t("manageSupplierRelationships", "Manage supplier relationships and performance")}

          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchVendors} className="gap-2">
            <RefreshCw className="h-4 w-4" />{t("refresh", "Refresh")}

          </Button>
          <Button onClick={() => setShowAddDialog(true)} className="gap-2 bg-indigo-600 hover:bg-indigo-700">
            <Plus className="h-4 w-4" />{t("addVendor", "Add Vendor")}

          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">{t("totalVendors", "Total Vendors")}</p>
                <p className="text-2xl font-bold">{vendors.length}</p>
              </div>
              <Users className="h-8 w-8 text-indigo-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">{t("activeVendors", "Active Vendors")}</p>
                <p className="text-2xl font-bold">{activeVendors}</p>
              </div>
              <CheckCircle2 className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">{t("totalSpend", "Total Spend")}</p>
                <p className="text-2xl font-bold">${totalSpend.toLocaleString()}</p>
              </div>
              <TrendingUp className="h-8 w-8 text-teal-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search and Filter */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search vendors..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9" />
          
        </div>
        <div className="flex gap-2">
          {['all', 'active', 'inactive'].map((status) =>
          <Button
            key={status}
            variant={statusFilter === status ? 'default' : 'outline'}
            onClick={() => setStatusFilter(status)}
            size="sm"
            className="capitalize">
            
              {status}
            </Button>
          )}
        </div>
      </div>

      {/* Vendor Grid */}
      {filteredVendors.length === 0 ?
      <Card>
          <CardContent className="flex flex-col items-center justify-center py-16">
            <Users className="h-16 w-16 text-slate-600 mb-4" />
            <p className="text-slate-400 text-lg mb-4">{t("noVendorsFound", "No vendors found")}</p>
            <Button onClick={() => setShowAddDialog(true)}>
              <Plus className="h-4 w-4 mr-2" />{t("addYourFirst", "Add your first vendor")}

          </Button>
          </CardContent>
        </Card> :

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {filteredVendors.map((vendor) =>
        <Card key={vendor.id} className="hover:border-indigo-500/50 transition-colors">
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-lg bg-indigo-500/20 flex items-center justify-center">
                      <Building2 className="h-5 w-5 text-indigo-500" />
                    </div>
                    <div>
                      <CardTitle className="text-base">{vendor.name}</CardTitle>
                      <p className="text-sm text-muted-foreground">{vendor.code}</p>
                    </div>
                  </div>
                  <Badge variant={vendor.is_active ? 'default' : 'secondary'}>
                    {vendor.is_active ? 'Active' : 'Inactive'}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 text-sm">
                  {vendor.category &&
              <div className="flex items-center gap-2 text-muted-foreground">
                      <Package className="h-4 w-4" />
                      <span>{vendor.category}</span>
                    </div>
              }
                  {vendor.contact_email &&
              <div className="flex items-center gap-2 text-muted-foreground">
                      <Mail className="h-4 w-4" />
                      <span className="truncate">{vendor.contact_email}</span>
                    </div>
              }
                  {vendor.contact_phone &&
              <div className="flex items-center gap-2 text-muted-foreground">
                      <Phone className="h-4 w-4" />
                      <span>{vendor.contact_phone}</span>
                    </div>
              }
                  {vendor.address &&
              <div className="flex items-center gap-2 text-muted-foreground">
                      <MapPin className="h-4 w-4" />
                      <span className="truncate">{vendor.address}</span>
                    </div>
              }
                </div>
                <div className="mt-4 pt-4 border-t border-slate-700 flex items-center justify-between">
                  <div className="flex items-center gap-1 text-yellow-500">
                    <Star className="h-4 w-4 fill-yellow-500" />
                    <span className="text-sm font-medium">{vendor.rating?.toFixed(1) || 'N/A'}</span>
                  </div>
                  <span className="text-sm text-muted-foreground">
                    ${(vendor.total_spend || 0).toLocaleString()}{t("spend", "spend")}
              </span>
                </div>
              </CardContent>
            </Card>
        )}
        </div>
      }

      {/* Add Vendor Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle>{t("addNewVendor", "Add New Vendor")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-slate-400 mb-1 block">{t("vendorName", "Vendor Name *")}</label>
                <Input
                  value={newVendor.name}
                  onChange={(e) => setNewVendor((prev) => ({ ...prev, name: e.target.value }))}
                  placeholder="Company Name"
                  className="bg-slate-900 border-slate-600" />
                
              </div>
              <div>
                <label className="text-sm text-slate-400 mb-1 block">{t("vendorCode", "Vendor Code *")}</label>
                <Input
                  value={newVendor.code}
                  onChange={(e) => setNewVendor((prev) => ({ ...prev, code: e.target.value }))}
                  placeholder="VEND001"
                  className="bg-slate-900 border-slate-600" />
                
              </div>
            </div>
            <div>
              <label className="text-sm text-slate-400 mb-1 block">{t("category", "Category")}</label>
              <Input
                value={newVendor.category}
                onChange={(e) => setNewVendor((prev) => ({ ...prev, category: e.target.value }))}
                placeholder="Raw Materials, Services, etc."
                className="bg-slate-900 border-slate-600" />
              
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-slate-400 mb-1 block">{t("email", "Email")}</label>
                <Input
                  type="email"
                  value={newVendor.contact_email}
                  onChange={(e) => setNewVendor((prev) => ({ ...prev, contact_email: e.target.value }))}
                  placeholder="vendor@example.com"
                  className="bg-slate-900 border-slate-600" />
                
              </div>
              <div>
                <label className="text-sm text-slate-400 mb-1 block">{t("phone", "Phone")}</label>
                <Input
                  value={newVendor.contact_phone}
                  onChange={(e) => setNewVendor((prev) => ({ ...prev, contact_phone: e.target.value }))}
                  placeholder="+1 234 567 8900"
                  className="bg-slate-900 border-slate-600" />
                
              </div>
            </div>
            <div>
              <label className="text-sm text-slate-400 mb-1 block">{t("address", "Address")}</label>
              <Input
                value={newVendor.address}
                onChange={(e) => setNewVendor((prev) => ({ ...prev, address: e.target.value }))}
                placeholder="123 Business St, City, Country"
                className="bg-slate-900 border-slate-600" />
              
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>{t("cancel", "Cancel")}</Button>
            <Button onClick={handleAddVendor} className="bg-indigo-600 hover:bg-indigo-700">{t("addVendor", "Add Vendor")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>);

};

export default Vendors;